import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';

export const REDACTED = 'REDACTED';

const SENSITIVE_QUERY_KEY_PATTERN =
  /plate|email|phone|search|address|token|(^|_)(first_|last_|full_)?name$|(first|last|full)Name$|(^|_)vin$|(^|_)(q|query)$|^(code|state|invitation|ticket|error_description)$|access_?code|secret|password|signature/i;

const EMAIL_VALUE_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

const EMAIL_IN_TEXT_PATTERN =
  /[^\s@"'<>()[\]{},;:]+@[^\s@"'<>()[\]{},;:]+\.[a-z]{2,}/gi;

const BEARER_PATH_KEYWORDS = new Set(['upload', 'upload-sessions']);

function safeDecode(segment: string) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function scrubBearerPathSegments(path: string) {
  const segments = path.split('/');
  return segments
    .map((segment, index) => {
      const previous = index > 0 ? segments[index - 1] : undefined;
      return previous !== undefined &&
        segment !== '' &&
        BEARER_PATH_KEYWORDS.has(safeDecode(previous).toLowerCase())
        ? REDACTED
        : segment;
    })
    .join('/');
}

function isSensitiveQueryParam(key: string, value: string) {
  return (
    SENSITIVE_QUERY_KEY_PATTERN.test(key) || EMAIL_VALUE_PATTERN.test(value)
  );
}

function scrubQueryString(query: string) {
  return query
    .split('&')
    .map((pair) => {
      const separatorIndex = pair.indexOf('=');
      if (separatorIndex === -1) return pair;
      const rawKey = pair.slice(0, separatorIndex);
      const rawValue = pair.slice(separatorIndex + 1);
      let key = rawKey;
      let value = rawValue;
      try {
        key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
        value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
      } catch {
        return `${rawKey}=${REDACTED}`;
      }
      return isSensitiveQueryParam(key, value) && rawValue !== ''
        ? `${rawKey}=${REDACTED}`
        : pair;
    })
    .join('&');
}

export function scrubUrl(url: string): string {
  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex + 1);

  const queryIndex = withoutHash.indexOf('?');
  const base =
    queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? null : withoutHash.slice(queryIndex + 1);

  const scrubbedBase = scrubBearerPathSegments(base);
  const scrubbedQuery = query === null ? '' : `?${scrubQueryString(query)}`;
  const scrubbedHash =
    hash === '' ? '' : `#${hash.includes('=') ? scrubQueryString(hash) : hash}`;

  return `${scrubbedBase}${scrubbedQuery}${scrubbedHash}`;
}

export function scrubText(text: string): string {
  return text.replace(EMAIL_IN_TEXT_PATTERN, REDACTED);
}

/**
 * Datadog only applies `beforeSend` mutations to an allowlist of fields per
 * event type, so only those URL fields are rewritten here.
 */
export function scrubRumEvent(event: RumEvent): boolean {
  if (event.view.url) event.view.url = scrubUrl(event.view.url);
  if (event.view.name) event.view.name = scrubUrl(event.view.name);
  if (event.view.referrer) event.view.referrer = scrubUrl(event.view.referrer);

  if (event.type === 'resource') {
    event.resource.url = scrubUrl(event.resource.url);
  }
  if (event.type === 'error') {
    event.error.message = scrubText(event.error.message);
    if (event.error.stack) event.error.stack = scrubText(event.error.stack);
    if (event.error.resource) {
      event.error.resource.url = scrubUrl(event.error.resource.url);
    }
  }
  return true;
}

export const RUM_PRIVACY_CONFIG = {
  defaultPrivacyLevel: 'mask',
  enablePrivacyForActionName: true,
  beforeSend: scrubRumEvent,
  startSessionReplayRecordingManually: true,
} satisfies Pick<
  RumInitConfiguration,
  | 'defaultPrivacyLevel'
  | 'enablePrivacyForActionName'
  | 'beforeSend'
  | 'startSessionReplayRecordingManually'
>;

export function toRumUser(userId: string | undefined) {
  return { id: userId ?? 'unknown' };
}
